'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Link from 'next/link';
import { MessageCircle } from 'lucide-react';
import { ConciergeEnquiryDialog } from './ConciergeEnquiryDialog';
import { useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

/**
 * Signed-out visitors sign in first, then return to the standalone request
 * form page (mirrors the directory "get listed" flow).
 */
const DEFAULT_SIGN_IN_HREF = '/auth/signin?redirect=/get-ideas';
interface ConciergeEnquiryButtonProps {
  label?: string;
  /** Where signed-out visitors go before they can send a request. */
  signInHref?: string;
  /**
   * When true (default), signed-out visitors are sent to {@link signInHref}
   * before they can send a request. When false, signed-out visitors open the
   * request dialog directly without signing in.
   */
  userSessionRequired?: boolean;
  /** Hide the leading icon (e.g. for a plain page CTA). */
  hideIcon?: boolean;
  className?: string;
  /** Called after a successful submission so a parent can show confirmation. */
  onSuccess?: () => void;
}

/**
 * Primary concierge call-to-action. Signed-in users open the request
 * {@link ConciergeEnquiryDialog}. Signed-out users are sent to sign in first
 * when {@link userSessionRequired} is true (the default); otherwise they open
 * the dialog directly. Reused by the home {@link SizzleConciergeSection} and
 * the /concierge page so the trigger behaves identically wherever it appears.
 */
export function ConciergeEnquiryButton(t0) {
  const $ = _c(16);
  const {
    label: t1,
    signInHref: t2,
    userSessionRequired: t3,
    hideIcon: t4,
    className,
    onSuccess
  } = t0;
  const label = t1 === undefined ? "Get a free tailored analysis and suggestions" : t1;
  const signInHref = t2 === undefined ? DEFAULT_SIGN_IN_HREF : t2;
  const userSessionRequired = t3 === undefined ? true : t3;
  const hideIcon = t4 === undefined ? false : t4;
  const {
    data: session
  } = useSession();
  const isLoggedIn = !!session?.user;
  const [dialogOpen, setDialogOpen] = useState(false);
  const shouldGate = userSessionRequired && !isLoggedIn;
  let t5;
  if ($[0] !== hideIcon) {
    t5 = !hideIcon && <MessageCircle className="h-5 w-5" aria-hidden="true" />;
    $[0] = hideIcon;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== label || $[3] !== t5) {
    t6 = <>{t5}{label}</>;
    $[2] = label;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const content = t6;
  let t7;
  if ($[5] !== className || $[6] !== content || $[7] !== shouldGate || $[8] !== signInHref) {
    t7 = shouldGate ? <Link href={signInHref} className={cn("inline-flex", className)}>{content}</Link> : <button type="button" onClick={() => setDialogOpen(true)} className={className}>{content}</button>;
    $[5] = className;
    $[6] = content;
    $[7] = shouldGate;
    $[8] = signInHref;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== dialogOpen || $[11] !== onSuccess) {
    t8 = <ConciergeEnquiryDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={onSuccess} />;
    $[10] = dialogOpen;
    $[11] = onSuccess;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}